import { useUnit } from 'effector-react';
import { useEffect, useMemo, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { $cmdkOpen, $cmdkQuery, cmdkClosed, cmdkQueryChanged } from '@/model/cmdk';
import { navigated, type View } from '@/model/router';
import { themeToggled } from '@/model/theme';
import { modalOpened } from '@/model/modals';
import { verifiedSet } from '@/model/kyc';
import { toastSent } from '@/model/toasts';

const fade = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.fx.overlay};
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 14vh;
  z-index: 130;
  animation: ${fade} ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out};
`;

const Panel = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadows.sh3};
  overflow: hidden;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  border-bottom: 1px solid ${({ theme }) => theme.line.soft};
`;

const Glyph = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
  color: ${({ theme }) => theme.ink[4]};
`;

const Input = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: ${({ theme }) => theme.typography.fontFamilies.sans};
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
  color: ${({ theme }) => theme.ink[1]};
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.ink[4]};
  }
`;

const List = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[2]};
`;

const Group = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.ink[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[2]}`};
`;

const Item = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.radii.xs};
  font-size: ${({ theme }) => theme.typography.fontSizes.uiLg};
  color: ${({ theme }) => theme.ink[2]};
  cursor: pointer;
  text-align: left;
  background: transparent;
  border: none;

  &:hover {
    background: ${({ theme }) => theme.tone.halo.tint};
    color: ${({ theme }) => theme.ink[1]};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.tone.halo.tint};
      color: ${theme.ink[1]};
    `}
`;

const Ic = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: ${({ theme }) => theme.surface.deep};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  color: ${({ theme }) => theme.ink[3]};
`;

const Tail = styled.span`
  margin-left: auto;
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[4]};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
`;

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-top: 1px solid ${({ theme }) => theme.line.soft};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[4]};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
`;

type Cmd =
  | { type: 'nav'; view: View; ic: string; label: string; tail?: string; group: 'навигация' }
  | { type: 'theme'; ic: string; label: string; tail?: string; group: 'действия' }
  | { type: 'share'; ic: string; label: string; tail?: string; group: 'действия' }
  | { type: 'example'; ic: string; label: string; tail?: string; group: 'действия' }
  | { type: 'reset-kyc'; ic: string; label: string; tail?: string; group: 'действия' };

const COMMANDS: Cmd[] = [
  { type: 'nav', view: 'landing', ic: '⌂', label: 'Главная', tail: 'g · h', group: 'навигация' },
  { type: 'nav', view: 'onboard', ic: '+', label: 'Начать отчёт', tail: 'g · o', group: 'навигация' },
  { type: 'nav', view: 'kyc', ic: 'id', label: 'Подтвердить личность', tail: 'g · k', group: 'навигация' },
  { type: 'nav', view: 'report', ic: '≈', label: 'Открыть отчёт', tail: 'g · r', group: 'навигация' },
  { type: 'theme', ic: '◐', label: 'Переключить тему', tail: 't', group: 'действия' },
  { type: 'share', ic: '↗', label: 'Поделиться отчётом', tail: 's', group: 'действия' },
  { type: 'example', ic: 'i', label: 'Посмотреть пример', tail: 'e', group: 'действия' },
  { type: 'reset-kyc', ic: '×', label: 'Сбросить верификацию', tail: 'r', group: 'действия' },
];

export const CommandPalette = () => {
  const [open, query] = useUnit([$cmdkOpen, $cmdkQuery]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cmdkClosed();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return COMMANDS.filter((c) => !q || c.label.toLowerCase().includes(q));
  }, [query]);

  if (!open) return null;

  const run = (c: Cmd) => {
    cmdkClosed();
    if (c.type === 'nav') navigated(c.view);
    if (c.type === 'theme') themeToggled();
    if (c.type === 'share') {
      navigated('report');
      window.setTimeout(() => modalOpened({ kind: 'share' }), 400);
    }
    if (c.type === 'example') modalOpened({ kind: 'example' });
    if (c.type === 'reset-kyc') {
      verifiedSet(false);
      toastSent('Верификация сброшена');
    }
  };

  const groups = filtered.reduce<Record<string, Cmd[]>>((acc, c) => {
    (acc[c.group] ??= []).push(c);
    return acc;
  }, {});

  return (
    <Backdrop onClick={() => cmdkClosed()}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <InputRow>
          <Glyph>поиск</Glyph>
          <Input
            ref={inputRef}
            placeholder="перейти, выполнить, открыть…"
            value={query}
            onChange={(e) => cmdkQueryChanged(e.target.value)}
          />
        </InputRow>
        <List>
          {Object.entries(groups).map(([grp, items]) => (
            <div key={grp}>
              <Group>{grp}</Group>
              {items.map((c, i) => (
                <Item key={`${grp}-${i}`} onClick={() => run(c)}>
                  <Ic>{c.ic}</Ic>
                  {c.label}
                  {c.tail && <Tail>{c.tail}</Tail>}
                </Item>
              ))}
            </div>
          ))}
        </List>
        <Foot>
          <span>↑↓ навигация · ↵ выбор · esc закрыть</span>
          <span>lume · v 0.3</span>
        </Foot>
      </Panel>
    </Backdrop>
  );
};
